<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useFetch } from '@/composables/useFetch'
import type { GeneratedEventModel, SuggestEvent } from '@/types/event'
import LoadingLottie from '@/components/LoadingLottie.vue'
import RefreshIcon from '@/components/svg/RefreshIcon.vue'
import { eventService } from '@/services/event'
import ArrowRight from '@/components/svg/ArrowRight.vue'
import type { Chat, Message } from '@/types'
import { mapMessageToXML } from '@/utils/textToMessages'
import { useKeyboardStore } from '@/store/keyboard'

interface Props {
  chat: Chat
  messages: Message[]
  isPreview: boolean
}

interface Emits {
  (e: 'applyEvent', event: GeneratedEventModel): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const keyboardStore = useKeyboardStore()

const { data, loading, error, fetchData } = useFetch<GeneratedEventModel[]>()
const eventInputRef = ref('')
const generatedEvents = ref<SuggestEvent[]>([])
onMounted(() => {
  fetchData(() => fetchGeneratedEvents())
})

watch(() => loading.value, (val) => {
  if (!val) {
    console.debug(data.value)
    const events: SuggestEvent[] = data.value?.map((event) => {
      return {
        event: event.eventDescription,
      }
    }) || []

    console.debug(events)
    generatedEvents.value = events
  }
  else {
    console.debug('Start Loading')
  }
})

watch(() => data.value, (val) => {
  // map GeneratedEventWrapper to SuggestEvent
  console.debug(val)
})

function onApplyEvent(event: GeneratedEventModel) {
  emit('applyEvent', event)
}

async function fetchGeneratedEvents(retry?: boolean) {
  return await eventService.generateEventSuggestion({
    universeId: props.chat.universe.universeId,
    eventOrder: props.chat.universe.events.length || 0,
    chatHistory: mapMessageToXML(props.messages),
    preview: props.isPreview,
    retry,
  })
}
</script>

<template>
  <div class="flex justify-center flex-col align-center h-full">
    <div v-if="loading">
      <loading-lottie />
      <p class="text-4 fw-700 color-#BDBDBD text-center">
        Generating plot options for the next event
      </p>
    </div>
    <div v-else-if="error">
      {{ error.value }}
    </div>
    <div
      v-else
      :class="`flex flex-col flex-grow ${keyboardStore.isTyping ? 'max-h-10vh' : 'max-h-50vh'} overflow-y-auto`"
    >
      <div
        v-if="!keyboardStore.isTyping"
        class="flex flex-col flex-grow gap-2"
      >
        <div
          v-for="(event, index) in generatedEvents"
          :key="index"
          class="flex flex-col px-4 py-1 rd-4 border-solid border-width-1 border-#BDBDBD bg-#594500 event-card"
          @click="() => {
            if (data) {
              onApplyEvent(data[index])
            }
          }"
        >
          <div class="flex items-center">
            <p class="text-4 fw-400 line-height-normal">
              {{ event.event }}
            </p>
            <arrow-right class="w-4 h-4" />
          </div>
        </div>
      </div>
    </div>
    <button
      v-if="!loading && !keyboardStore.isTyping"
      class="btn-secondary mt-2 min-h-12"
      @click="() => {
        eventInputRef = ''
        fetchData(() => fetchGeneratedEvents(true))
      }"
    >
      <refresh-icon
        style="width: 24px;"
      />
      Regenerate
    </button>
  </div>
</template>

<style scoped>
.event-card {
  background: rgba(255, 255, 255, 0.24)
}

.max-h-10vh {
  max-height: 10vh;
}

.max-h-50vh {
  max-height: 50vh;
}
</style>
